import { inject, Injectable } from '@angular/core';
import { GenericLogger } from '@scalefast/ecommerce-core';
import { GenericDynamicComponent } from 'src/app/core/classes/DynamicComponent';
import { PageComponentService, CUSTOM_PAGE_LAYOUT_TYPES, PageLayoutComponent } from 'src/app/core/interfaces/page-creator';
import { PAGE_LAYOUT_TYPES } from 'src/app/core/interfaces/page-creator/to-pandora-index';

@Injectable({
  providedIn: 'root',
})
export class PageLayoutComponentService implements PageComponentService<PageLayoutComponent> {
  #pageLayoutComponents: { [id: string]: () => Promise<GenericDynamicComponent> } = {};
  #logger = inject(GenericLogger);

  constructor() {
    this.#registerPageLayoutComponents();
  }

  async getComponent(id: string, pageLayoutComponent?: PageLayoutComponent): Promise<GenericDynamicComponent | undefined> {
    const loadComponentFn = this.#pageLayoutComponents[id];
    if (!loadComponentFn) {
      this.#logPageLayoutComponentError(id, pageLayoutComponent);
      return undefined;
    }

    const module = await loadComponentFn();
    this.#logger.debug({ msg: `Loaded page layout component of type: ${id}` });
    return module;
  }

  #logPageLayoutComponentError(id: string, pageLayoutComponent?: PageLayoutComponent) {
    const msg = `Attempt to load an unknown page layout component type, this is usually a unknown/misconfigured page layout component in the page-creator builder model. ID: ${id}`;
    this.#logger.error({ msg, data: pageLayoutComponent });
  }

  #addPageLayout(pageLayout: PageLayoutComponent, loadComponentFn: () => any): void {
    let id: string = pageLayout.id;
    if (this.#pageLayoutComponents[id]) {
      console.warn(`Registering Page Layout Component with repeated ID: ${id}`);
      return;
    }

    this.#pageLayoutComponents[id] = loadComponentFn;
  }

  #registerPageLayoutComponents() {
    this.#addPageLayout({ id: PAGE_LAYOUT_TYPES.DEFAULT }, () =>
      import('../../../pages/default/page-creator.component').then((m) => m.AppPageCreatorComponent),
    );
    this.#addPageLayout({ id: CUSTOM_PAGE_LAYOUT_TYPES.BEST_OFFERS }, () =>
      import('../../../pages/best-offers-page/best-offers-page.component').then((m) => m.AppBestOffersPageComponent),
    );
    this.#addPageLayout({ id: CUSTOM_PAGE_LAYOUT_TYPES.CONTENTFUL_PAGE }, () =>
      import('../../../pages/contentful-page/contentful-page.component').then((m) => m.AppContentfulPageComponent),
    );
  }
}
